/**
 * useDeletePerformance — Delete a performance from the unified
 * `performances` table along with its `performance_events` junction
 * rows.
 *
 * Order: junctions first (no FK cascade), then the performance itself.
 *
 * Caches invalidated on success:
 *   - performancesKeys.all
 *   - performanceEventsKeys.all
 *   - rodeoOpsKeys.all
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PerformancesClient } from '../api/generated_clients/generated-performances-api-client';
import { PerformanceEventsClient } from '../api/generated_clients/generated-performance_events-api-client';
import { performancesKeys } from '../api/generated_hooks/generated-performances-hooks';
import { performanceEventsKeys } from '../api/generated_hooks/generated-performance_events-hooks';
import { rodeoOpsKeys } from '../api/extensions/useRodeoOps';

/**
 * Delete a performance by id and invalidate every cache that could
 * render it.
 *
 * @returns React Query mutation: `mutate(id)` / `mutateAsync(id)`.
 */
export function useDeletePerformance() {
  const queryClient = useQueryClient();
  const perfClient = new PerformancesClient();
  const junctionClient = new PerformanceEventsClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // 1) Wipe the performance_events junction.
      const links = (await junctionClient.getAll({
        filters: { performance_id: id },
      } as any)) as any;
      const linkRows: Array<{ id?: string }> = Array.isArray(links)
        ? links
        : (links?.data ?? []);
      await Promise.all(
        linkRows
          .map((l) => l?.id)
          .filter((rid): rid is string => typeof rid === 'string')
          .map((rid) => junctionClient.delete(rid)),
      );

      // 2) Delete the performances row.
      return perfClient.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performancesKeys.all });
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
      queryClient.invalidateQueries({ queryKey: rodeoOpsKeys.all });
    },
  });
}
